import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import TrustIndicators from './components/TrustIndicators';
import ServicesShowcase from './components/ServicesShowcase';
import GlobalNetwork from './components/GlobalNetwork';
import TrackingFeature from './components/TrackingFeature';
import Statistics from './components/Statistics';
import WhyChooseUs from './components/WhyChooseUs';
import TestimonialsCarousel from './components/TestimonialsCarousel';
import Partners from './components/Partners';
import NewsUpdates from './components/NewsUpdates';
import CTASection from './components/CTASection';

const HomePage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://track-verse.com';

  useEffect(() => {
    // Title & primary meta
    document.title = 'TrackVerse Ajaccio — Suivi Colis & Logistique Internationale Temps Réel';
    document.documentElement.lang = 'fr';

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    setMeta('description', 'TrackVerse, expert logistique à Ajaccio : suivi colis en temps réel dans 50+ pays, fret maritime, aérien, routier et ferroviaire. Suivez votre envoi instantanément avec TrackVerse.');
    setMeta('keywords', 'suivi colis, logistique internationale, TrackVerse Ajaccio, tracking envoi temps réel, fret maritime aérien routier');
    setMeta('author', 'TrackVerse — Michael Ashley');

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); (canonical as HTMLLinkElement).rel = 'canonical'; document.head.appendChild(canonical); }
    (canonical as HTMLLinkElement).href = `${siteUrl}/`;

    // OG tags
    setMeta('og:title', 'TrackVerse — Suivi Colis & Logistique Mondiale | Ajaccio', true);
    setMeta('og:description', 'Suivi colis en temps réel dans 50+ pays. Fret maritime, aérien, routier. TrackVerse, votre partenaire logistique de confiance depuis Ajaccio, Corse.', true);
    setMeta('og:url', `${siteUrl}/`, true);
    setMeta('og:type', 'website', true);
    setMeta('og:image', `${siteUrl}/og-image.jpg`, true);
    setMeta('og:locale', 'fr_FR', true);

    // Twitter
    setMeta('twitter:title', 'TrackVerse — Suivi Colis & Logistique Mondiale');
    setMeta('twitter:description', 'Suivi colis en temps réel dans 50+ pays. Fret maritime, aérien, routier. TrackVerse depuis Ajaccio.');
    setMeta('twitter:image', `${siteUrl}/og-image.jpg`);

    // Add JSON-LD schemas
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${siteUrl}/#organization`,
      "name": "TrackVerse",
      "url": siteUrl,
      "logo": { "@type": "ImageObject", "url": `${siteUrl}/logo.png`, "width": 200, "height": 60 },
      "description": "TrackVerse est une plateforme de logistique internationale et de suivi colis en temps réel opérant dans 50+ pays.",
      "address": { "@type": "PostalAddress", "streetAddress": "123 Avenue Napoléon", "addressLocality": "Ajaccio", "addressRegion": "Corse-du-Sud", "postalCode": "20000", "addressCountry": "FR" },
      "geo": { "@type": "GeoCoordinates", "latitude": "41.9267", "longitude": "8.7369" },
      "contactPoint": { "@type": "ContactPoint", "telephone": "+33-4-95-00-00-00", "contactType": "Customer Service", "email": "info.trackverse@gmail.com", "availableLanguage": ["French", "English", "Spanish", "German", "Arabic"] },
      "founder": { "@type": "Person", "name": "Michael Ashley" },
      "foundingDate": "2020",
      "sameAs": ["https://facebook.com/trackverse", "https://twitter.com/trackverse", "https://linkedin.com/company/trackverse"],
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "1247", "bestRating": "5" }
    };

    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      "name": "TrackVerse",
      "url": siteUrl,
      "inLanguage": ["fr", "en"],
      "potentialAction": { "@type": "SearchAction", "target": { "@type": "EntryPoint", "urlTemplate": `${siteUrl}/tracking?q={search_term_string}` }, "query-input": "required name=search_term_string" }
    };

    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Logistique Internationale & Suivi Colis",
      "provider": { "@type": "Organization", "name": "TrackVerse" },
      "description": "Services complets de logistique internationale : fret maritime, aérien, routier et ferroviaire avec suivi en temps réel.",
      "areaServed": { "@type": "Place", "name": "Worldwide — 50+ countries" },
      "serviceType": ["Fret maritime", "Fret aérien", "Transport routier", "Logistique ferroviaire", "Suivi colis"]
    };

    const schemas = [
      { id: 'home-organization', data: organizationSchema },
      { id: 'home-website', data: websiteSchema },
      { id: 'home-service', data: serviceSchema }
    ];

    schemas.forEach(({ id, data }) => {
      let script = document.querySelector(`script[data-schema="${id}"]`);
      if (!script) { script = document.createElement('script'); script.setAttribute('type', 'application/ld+json'); script.setAttribute('data-schema', id); document.head.appendChild(script); }
      script.textContent = JSON.stringify(data);
    });

    return () => {
      schemas.forEach(({ id }) => { const s = document.querySelector(`script[data-schema="${id}"]`); if (s) s.remove(); });
    };
  }, [siteUrl]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <HeroSection />
      <TrustIndicators />
      <ServicesShowcase />
      <GlobalNetwork />
      <TrackingFeature />
      <Statistics />
      <WhyChooseUs />
      <TestimonialsCarousel />
      <Partners />
      <NewsUpdates />
      <CTASection />
      <Footer />
    </div>
  );
};

export default HomePage;
